import * as React from "react";
import { useTheme } from "./useTheme";
import type { FontId, FontSizeId, ThemeId, ThemeMode } from "./theme";

type ThemeContextValue = ReturnType<typeof useTheme>;

const ThemeContext = React.createContext<ThemeContextValue | null>(null);

export function ThemeProvider({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  const theme = useTheme();
  const rootRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (rootRef.current) theme.applyToRoot(rootRef.current);
  }, [theme]);

  return (
    <ThemeContext.Provider value={theme}>
      <div
        ref={rootRef}
        data-theme={theme.theme}
        data-font={theme.font}
        data-mode={theme.resolvedMode}
        data-fs={theme.fontSize}
        data-hc={theme.highContrast ? "true" : "false"}
        style={{
          height: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          background: "var(--bg)",
          color: "var(--body)",
          fontFamily: "var(--font-sans), system-ui, sans-serif",
          ...style,
        }}
      >
        {children}
      </div>
    </ThemeContext.Provider>
  );
}

export function useThemeContext() {
  const ctx = React.useContext(ThemeContext);
  if (!ctx) throw new Error("useThemeContext must be used within ThemeProvider");
  return ctx;
}

export type { FontId, FontSizeId, ThemeId, ThemeMode };
